import { MenuItem, OutlinedInput, Select } from "@mui/material";
import type { CylinerMeasureType, DuoMeasurementValues, MeasurementValues, MeasureSettings } from "@novorender/api";
import { ChevronDown, Info } from "@procore/core-icons";
import {
    Box,
    Button,
    Card,
    colors,
    H3,
    spacing,
    Table,
    Title,
    Tooltip as ProcoreTooltip,
    Typography,
} from "@procore/core-react";
import type { ReadonlyVec3 } from "gl-matrix";
import { vec3 } from "gl-matrix";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { Tooltip } from "components";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import type { ExtendedMeasureEntity } from "types/misc";
import { getMeasurementValueKind, measureObjectIsVertex } from "utils/misc";

import { cylinderOptions } from "./config";
import { measureActions, selectMeasure } from "./measureSlice";
import { PlanarDiff } from "./planarDiff";
import { useMeasuredObjectData } from "./useMeasuredObjectData";
import { calculateSlope } from "./utils";

function MeasurementTable({ start, end }: { start: ReadonlyVec3; end: ReadonlyVec3 }) {
    const { t } = useTranslation();
    const delta = vec3.sub(vec3.create(), end, start);
    const uom = useUom();

    return (
        <Table.Container style={{ width: "100%" }}>
            <Table style={{ tableLayout: "fixed", width: "100%" }}>
                <Table.Header style={{ backgroundColor: colors.gray90 }}>
                    <Table.HeaderRow>
                        <Table.HeaderCell style={{ width: "25%" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                {t("name")}
                            </Typography>
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ width: "25%", textAlign: "center" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                X
                            </Typography>
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ width: "25%", textAlign: "center" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                Y
                            </Typography>
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ width: "25%", textAlign: "center" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                Z
                            </Typography>
                        </Table.HeaderCell>
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    <Table.BodyRow>
                        <Table.BodyCell>
                            <Table.TextCell>{t("start")}</Table.TextCell>
                        </Table.BodyCell>
                        {start.map((v, idx) => (
                            <Table.BodyCell key={idx} style={{ textAlign: "right" }}>
                                <Table.TextCell>
                                    {convertAndFormat(uom, v, UnitType.length, { decimals: 3 })}
                                </Table.TextCell>
                            </Table.BodyCell>
                        ))}
                    </Table.BodyRow>
                    <Table.BodyRow>
                        <Table.BodyCell>
                            <Table.TextCell>{t("end")}</Table.TextCell>
                        </Table.BodyCell>
                        {end.map((v, idx) => (
                            <Table.BodyCell key={idx} style={{ textAlign: "right" }}>
                                <Table.TextCell>
                                    {convertAndFormat(uom, v, UnitType.length, { decimals: 3 })}
                                </Table.TextCell>
                            </Table.BodyCell>
                        ))}
                    </Table.BodyRow>
                    <Table.BodyRow>
                        <Table.BodyCell>
                            <Table.TextCell>
                                <Typography weight="semibold">{t("difference")}</Typography>
                            </Table.TextCell>
                        </Table.BodyCell>
                        <Table.BodyCell style={{ textAlign: "right" }}>
                            <Table.TextCell>
                                <Typography weight="semibold">
                                    {convertAndFormat(uom, Math.abs(delta[0]), UnitType.length, { decimals: 3 })}
                                </Typography>
                            </Table.TextCell>
                        </Table.BodyCell>
                        <Table.BodyCell style={{ textAlign: "right" }}>
                            <Table.TextCell>
                                <Typography weight="semibold">
                                    {convertAndFormat(uom, Math.abs(delta[1]), UnitType.length, { decimals: 3 })}
                                </Typography>
                            </Table.TextCell>
                        </Table.BodyCell>
                        <Table.BodyCell style={{ textAlign: "right" }}>
                            <Table.TextCell>
                                <Typography weight="semibold">
                                    {convertAndFormat(uom, Math.abs(delta[2]), UnitType.length, { decimals: 3 })}
                                </Typography>
                            </Table.TextCell>
                        </Table.BodyCell>
                    </Table.BodyRow>
                </Table.Body>
            </Table>
        </Table.Container>
    );
}

function VertexTable({ vertices, text }: { vertices: ReadonlyVec3[]; text?: string[] }) {
    const { t } = useTranslation();
    const uom = useUom();

    return (
        <Table.Container style={{ width: "100%" }}>
            <Table style={{ tableLayout: "fixed", width: "100%" }}>
                <Table.Header style={{ backgroundColor: colors.gray90 }}>
                    <Table.HeaderRow>
                        <Table.HeaderCell style={{ width: "25%" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                {t("name")}
                            </Typography>
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ width: "25%", textAlign: "center" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                X
                            </Typography>
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ width: "25%", textAlign: "center" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                Y
                            </Typography>
                        </Table.HeaderCell>
                        <Table.HeaderCell style={{ width: "25%", textAlign: "center" }}>
                            <Typography intent="small" weight="semibold" color="gray30">
                                Z
                            </Typography>
                        </Table.HeaderCell>
                    </Table.HeaderRow>
                </Table.Header>
                <Table.Body>
                    {vertices.map((p, i) => (
                        <Table.BodyRow key={i}>
                            <Table.BodyCell>
                                <Table.TextCell>{text && text.length > i ? text[i] : t("vertex")}</Table.TextCell>
                            </Table.BodyCell>
                            {p.map((v, idx) => (
                                <Table.BodyCell key={idx} style={{ textAlign: "right" }}>
                                    <Table.TextCell>
                                        {convertAndFormat(uom, v, UnitType.length, { decimals: 3 })}
                                    </Table.TextCell>
                                </Table.BodyCell>
                            ))}
                        </Table.BodyRow>
                    ))}
                </Table.Body>
            </Table>
        </Table.Container>
    );
}

export function MeasuredObject({ obj, idx }: { obj: ExtendedMeasureEntity; idx: number }) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { duoMeasurementValues, currentIndex } = useAppSelector(selectMeasure);
    const currentMeasureValues = duoMeasurementValues[currentIndex];
    const [expanded, setExpanded] = useState(true);
    const [componentsExpanded, setComponentsExpanded] = useState(false);
    const { currentMeasureObject, measureValues, isGenerated } = useMeasuredObjectData(obj, idx, currentIndex);

    const kind = !currentMeasureObject ? "" : measureValues ? getMeasurementValueKind(measureValues) : "point";

    const _idx = idx === 0 ? "measureInfoA" : "measureInfoB";
    const useCylinderMeasureSettings = currentMeasureValues && currentMeasureValues.result[_idx]?.validMeasureSettings;

    if (!obj || (!measureValues && !currentMeasureObject)) {
        return null;
    }

    const cardStyle = {
        marginBottom:
            measureValues &&
            "kind" in measureValues &&
            ((measureValues.kind === "line" && measureValues.start && measureValues.end) ||
                (measureValues.kind === "plane" && measureValues.vertices && measureValues.vertices.length > 0))
                ? 0
                : `${spacing.lg}px`,
        paddingTop: expanded ? spacing.md : spacing.sm,
        paddingBottom: expanded ? spacing.lg : spacing.sm,
        paddingLeft: spacing.lg,
        paddingRight: spacing.lg,
    };

    const titleStyle = {
        paddingBottom: expanded ? spacing.md : 0,
    };

    return (
        <>
            <Card style={cardStyle}>
                <Title style={titleStyle}>
                    <Title.Assets>
                        <Button
                            icon={<ChevronDown />}
                            onClick={() => setExpanded(!expanded)}
                            variant="tertiary"
                            size="sm"
                        />
                    </Title.Assets>
                    <Title.Text>
                        <H3>{t(kind.toLocaleLowerCase() || "loading")}</H3>
                    </Title.Text>
                    <Title.Actions>
                        {isGenerated && (
                            <ProcoreTooltip
                                placement="right"
                                overlay={
                                    <ProcoreTooltip.Content>
                                        <Typography intent="small" color="white">
                                            {t("parametricDataGenerated")}
                                        </Typography>
                                    </ProcoreTooltip.Content>
                                }
                            >
                                <Info style={{ color: colors.black, cursor: "pointer" }} />
                            </ProcoreTooltip>
                        )}
                    </Title.Actions>
                </Title>

                {expanded && (
                    <>
                        {!currentMeasureObject ? null : measureValues ? (
                            <MeasurementData
                                measureValues={measureValues}
                                useCylinderRelativeMeasureSettings={useCylinderMeasureSettings}
                                settings={obj?.settings}
                                onSettingsChange={(newValue) => {
                                    dispatch(
                                        measureActions.setSettings({
                                            idx,
                                            settings: {
                                                cylinderMeasure: newValue as CylinerMeasureType,
                                            },
                                        }),
                                    );
                                }}
                            />
                        ) : measureObjectIsVertex(currentMeasureObject) ? (
                            <VertexTable vertices={[currentMeasureObject.parameter]} />
                        ) : null}
                    </>
                )}
            </Card>

            {measureValues &&
                "kind" in measureValues &&
                ((measureValues.kind === "line" && measureValues.start && measureValues.end) ||
                    (measureValues.kind === "plane" &&
                        measureValues.vertices &&
                        measureValues.vertices.length > 0)) && (
                    <Card
                        style={{
                            marginTop: `${spacing.sm}px`,
                            marginBottom: `${spacing.lg}px`,
                            paddingTop: componentsExpanded ? spacing.md : spacing.sm,
                            paddingBottom: componentsExpanded ? spacing.lg : spacing.sm,
                            paddingLeft: spacing.lg,
                            paddingRight: spacing.lg,
                        }}
                    >
                        <Title style={{ paddingBottom: componentsExpanded ? spacing.md : 0 }}>
                            <Title.Assets>
                                <Button
                                    variant="tertiary"
                                    icon={<ChevronDown />}
                                    onClick={() => setComponentsExpanded(!componentsExpanded)}
                                    size="sm"
                                />
                            </Title.Assets>
                            <Title.Text>
                                <H3>{t("components")}</H3>
                            </Title.Text>
                        </Title>

                        {componentsExpanded && (
                            <>
                                {measureValues.kind === "line" && measureValues.start && measureValues.end && (
                                    <MeasurementTable start={measureValues.start} end={measureValues.end} />
                                )}
                                {measureValues.kind === "plane" &&
                                    measureValues.vertices &&
                                    measureValues.vertices.length > 0 && (
                                        <VertexTable vertices={measureValues.vertices} />
                                    )}
                            </>
                        )}
                    </Card>
                )}
        </>
    );
}

function MeasurementData({
    measureValues,
    useCylinderRelativeMeasureSettings,
    settings,
    onSettingsChange,
}: {
    measureValues: MeasurementValues;
    useCylinderRelativeMeasureSettings?: boolean;
    settings?: MeasureSettings;
    onSettingsChange?: (newValue: string) => void;
}) {
    const { t } = useTranslation();
    const uom = useUom();

    if (!("kind" in measureValues)) {
        return null;
    }

    const rows: { label: string; value: string | React.ReactNode }[] = [];

    switch (measureValues.kind) {
        case "line": {
            rows.push({
                label: t("length"),
                value: convertAndFormat(uom, measureValues.distance.valueOf(), UnitType.length, {
                    decimals: 3,
                }),
            });
            rows.push({
                label: t("slope"),
                value: calculateSlope(measureValues.start, measureValues.end),
            });
            break;
        }
        case "lineStrip": {
            if (measureValues.totalLength) {
                rows.push({
                    label: t("length"),
                    value: convertAndFormat(uom, measureValues.totalLength, UnitType.length, {
                        decimals: 3,
                    }),
                });
            }
            break;
        }
        case "arc": {
            rows.push({
                label: t("diameter"),
                value: convertAndFormat(uom, measureValues.radius.valueOf() * 2, UnitType.length, {
                    decimals: 3,
                }),
            });
            rows.push({
                label: t("totalAngle"),
                value: convertAndFormat(uom, Math.round(measureValues.totalAngle * (180 / Math.PI)), UnitType.angle),
            });
            break;
        }
        case "plane": {
            if (measureValues.height) {
                rows.push({
                    label: t("height"),
                    value: convertAndFormat(uom, measureValues.height, UnitType.length, { decimals: 3 }),
                });
            }
            if (measureValues.width) {
                rows.push({
                    label: t("width"),
                    value: convertAndFormat(uom, measureValues.width, UnitType.length, { decimals: 3 }),
                });
            }
            if (measureValues.errorMargin) {
                rows.push({
                    label: t("errorMargin"),
                    value: (
                        <Tooltip title={t("errorMarginMsg")} enterDelay={0}>
                            <span>
                                {convertAndFormat(uom, measureValues.errorMargin, UnitType.length, {
                                    decimals: 3,
                                })}
                            </span>
                        </Tooltip>
                    ),
                });
            }
            if (measureValues.area) {
                rows.push({
                    label: t("area"),
                    value: convertAndFormat(uom, measureValues.area.valueOf(), UnitType.area, { decimals: 3 }),
                });
            }
            if (measureValues.innerRadius) {
                rows.push({
                    label: t(measureValues.outerRadius ? "innerDiameter" : "diameter"),
                    value: convertAndFormat(
                        uom,
                        (measureValues.outerRadius
                            ? Math.min(measureValues.outerRadius as number, measureValues.innerRadius as number)
                            : measureValues.innerRadius) * 2,
                        UnitType.length,
                        { decimals: 3 },
                    ),
                });
            }
            if (measureValues.outerRadius) {
                rows.push({
                    label: t(measureValues.innerRadius ? "outerDiameter" : "diameter"),
                    value: convertAndFormat(
                        uom,
                        (measureValues.innerRadius
                            ? Math.max(measureValues.outerRadius as number, measureValues.innerRadius as number)
                            : measureValues.outerRadius) * 2,
                        UnitType.length,
                        { decimals: 3 },
                    ),
                });
            }
            if (measureValues.heightAboveXyPlane !== undefined) {
                rows.push({
                    label: t("elevation"),
                    value: convertAndFormat(uom, measureValues.heightAboveXyPlane, UnitType.length, {
                        decimals: 3,
                    }),
                });
            }
            break;
        }
        case "cylinder": {
            const distance = vec3.dist(measureValues.centerLineStart, measureValues.centerLineEnd);

            rows.push({
                label: t("diameter"),
                value: convertAndFormat(uom, measureValues.radius * 2, UnitType.length, { decimals: 3 }),
            });
            rows.push({
                label: t("length"),
                value: convertAndFormat(uom, distance, UnitType.length, { decimals: 3 }),
            });
            rows.push({
                label: t("slope"),
                value: calculateSlope(measureValues.centerLineStart, measureValues.centerLineEnd),
            });
            break;
        }
        case "polymesh": {
            if (measureValues.area) {
                rows.push({
                    label: t("area"),
                    value: convertAndFormat(uom, measureValues.area.valueOf(), UnitType.area, {
                        decimals: 3,
                    }),
                });
            }
            break;
        }
    }

    return (
        <>
            {measureValues.kind === "cylinder" && onSettingsChange && (
                <Box style={{ marginBottom: `${spacing.sm}px` }}>
                    <Typography
                        weight="semibold"
                        intent="small"
                        color="gray30"
                        style={{ marginBottom: `${spacing.xs}px` }}
                    >
                        {t("measureFrom")}
                    </Typography>
                    <Select
                        fullWidth
                        size="small"
                        value={settings?.cylinderMeasure ? settings.cylinderMeasure : "center"}
                        onChange={(event) => onSettingsChange(event.target.value)}
                        input={<OutlinedInput fullWidth />}
                    >
                        {cylinderOptions.map((opt) => (
                            <MenuItem
                                disabled={
                                    useCylinderRelativeMeasureSettings
                                        ? false
                                        : opt.val === "closest" || opt.val === "furthest"
                                }
                                key={opt.val}
                                value={opt.val}
                            >
                                {t(opt.labelKey)}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {rows.length > 0 && (
                <Box
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: `${spacing.md}px`,
                        width: "100%",
                    }}
                >
                    {rows.map((row, index) =>
                        row.label ? (
                            <Box key={index} style={{ display: "flex", flexDirection: "column" }}>
                                <Typography weight="semibold">{row.label}</Typography>
                                <Typography>{row.value}</Typography>
                            </Box>
                        ) : (
                            <Box key={index} style={{ gridColumn: "1 / -1", padding: `${spacing.xs}px 0` }}>
                                {row.value}
                            </Box>
                        ),
                    )}
                </Box>
            )}
        </>
    );
}

export function MeasuredResult({ duoMeasurementValues }: { duoMeasurementValues: DuoMeasurementValues }) {
    const { t } = useTranslation();
    const uom = useUom();
    const [expanded, setExpanded] = useState(true);
    const [componentsExpanded, setComponentsExpanded] = useState(true);

    const hasMeasurePoints = duoMeasurementValues.measureInfoA?.point && duoMeasurementValues.measureInfoB?.point;
    const pts = hasMeasurePoints
        ? [duoMeasurementValues.measureInfoA.point, duoMeasurementValues.measureInfoB.point]
        : duoMeasurementValues.normalPoints;

    const showSlope = hasMeasurePoints;
    const showPlanarDiff = hasMeasurePoints;
    const parameters = [
        duoMeasurementValues.measureInfoA?.parameter,
        duoMeasurementValues.measureInfoB?.parameter,
    ].filter((p) => typeof p === "number") as number[];

    const rows: { label: string; value: string | React.ReactNode }[] = [];

    if (duoMeasurementValues.distance) {
        rows.push({
            label: t("distance"),
            value: convertAndFormat(uom, duoMeasurementValues.distance, UnitType.length, { decimals: 3 }),
        });
    }

    if (duoMeasurementValues.angle) {
        rows.push({
            label: t("angle"),
            value: `${(duoMeasurementValues.angle.radians * (180 / Math.PI)).toFixed(3)}°`,
        });
    }

    if (duoMeasurementValues.normalDistance !== undefined) {
        rows.push({
            label: t("normalDistance"),
            value: convertAndFormat(uom, duoMeasurementValues.normalDistance, UnitType.length, {
                decimals: 3,
            }),
        });
    }

    if (showPlanarDiff && pts) {
        rows.push({
            label: "",
            value: <PlanarDiff start={pts[0]} end={pts[1]} />,
        });
    }

    parameters.forEach((p, idx, arr) => {
        rows.push({
            label: `${t("parameter")}${arr.length === 2 ? (idx === 0 ? "A" : "B") : ""}`,
            value: p.toFixed(3),
        });
    });

    if (showSlope && pts) {
        rows.push({
            label: t("slope"),
            value: calculateSlope(pts[0], pts[1]),
        });
    }

    const cardStyle = {
        marginBottom: `${spacing.lg}px`,
        paddingTop: expanded ? spacing.md : spacing.sm,
        paddingBottom: expanded ? spacing.lg : spacing.sm,
        paddingLeft: spacing.lg,
        paddingRight: spacing.lg,
    };

    const titleStyle = {
        paddingBottom: expanded ? spacing.md : 0,
    };

    return (
        <>
            <Card style={cardStyle}>
                <Title style={titleStyle}>
                    <Title.Assets>
                        <Button
                            variant="tertiary"
                            icon={<ChevronDown color={colors.gray50} />}
                            size="sm"
                            onClick={() => setExpanded(!expanded)}
                        />
                    </Title.Assets>
                    <Title.Text>
                        <H3>{t("result")}</H3>
                    </Title.Text>
                </Title>
                {expanded && (
                    <>
                        {rows.length > 0 && (
                            <Box
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: `${spacing.md}px`,
                                    width: "100%",
                                    marginBottom: pts ? `${spacing.sm}px` : 0,
                                }}
                            >
                                {rows.map((row, index) =>
                                    row.label ? (
                                        <Box key={index} style={{ display: "flex", flexDirection: "column" }}>
                                            <Typography weight="semibold">{row.label}</Typography>
                                            <Typography>{row.value}</Typography>
                                        </Box>
                                    ) : (
                                        <Box key={index} style={{ gridColumn: "1 / -1", padding: `${spacing.xs}px 0` }}>
                                            {row.value}
                                        </Box>
                                    ),
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Card>

            {pts && (
                <Card
                    style={{
                        marginBottom: `${spacing.sm}px`,
                        paddingTop: componentsExpanded ? spacing.md : spacing.sm,
                        paddingBottom: componentsExpanded ? spacing.lg : spacing.sm,
                        paddingLeft: spacing.lg,
                        paddingRight: spacing.lg,
                    }}
                >
                    <Title style={{ paddingBottom: componentsExpanded ? spacing.md : 0 }}>
                        <Title.Assets>
                            <Button
                                variant="tertiary"
                                icon={<ChevronDown />}
                                onClick={() => setComponentsExpanded(!componentsExpanded)}
                                size="sm"
                            />
                        </Title.Assets>
                        <Title.Text>
                            <H3>{t("components")}</H3>
                        </Title.Text>
                    </Title>

                    {componentsExpanded && <MeasurementTable start={pts[0]} end={pts[1]} />}
                </Card>
            )}
        </>
    );
}
